// src/components/games/Crossroads.tsx
import { logger } from '../../../utils/logger';
// Crossroads: The Divergent Realms TCG

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonButton,
  IonText,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonBadge,
  IonIcon,
  useIonToast,
  IonAlert,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonSelect,
  IonSelectOption,
  IonItem,
  IonLabel,
  IonPopover,
  IonList,
} from '@ionic/react';
import {
  heart,
  flash,
  waterOutline,
  flameOutline,
  flashOutline,
  shieldOutline,
  personOutline,
  alertCircleOutline,
  arrowBack,
  helpCircleOutline,
  ellipsisVertical,
  exitOutline,
  flagOutline,
} from 'ionicons/icons';
import './Crossroads.css';

import {
  GameState,
  Phase,
  Card,
  CardType,
  ElementType,
  CreatureCard,
  ManaSourceCard,
  ArtCard,
  CommanderCard,
  CommanderAbility,
  BattlefieldCreature,
  BattlefieldManaSource,
  CombatDeclaration,
  BlockDeclaration,
  hasTypeAdvantage,
  DeckDefinition,
  ModalChoiceType,
} from './types';
import { GameLogic, shuffleArray } from './gameLogic';
import { STARTER_DECKS, ALL_CARDS } from './cardData';
import { ENVIRONMENT_DECKS, ALL_ENVIRONMENT_CARDS } from './environmentDecks';
import { executeAITurn, getAIBlockDecision, AIDifficulty } from './aiPlayer';
import { ChallengePlayerModal } from './ChallengePlayerModal';
import GameEventLog from './GameEventLog';
import ChatOverlay from './ChatOverlay';
import ShieldIndicator from './ShieldIndicator';
import DiceRollIndicator from './DiceRollIndicator';
import TutorialOverlay, { TutorialStep } from './TutorialOverlay';
import { getPlayerShield, getCreatureShield } from './gameUtils';
import { ModalChoiceDialog } from './ModalChoiceDialog';
import { TargetSelector } from './TargetSelector';
import { DeckBuilder } from './DeckBuilder';
import { CardDisplay } from './CardDisplay';
import { CardDetailModal } from './CardDetailModal';
import ManaPoolDisplay from './ManaPoolDisplay';
import CardTooltip from './CardTooltip';
import CombatPreview from './CombatPreview';

type CombatStep = 'declare_attackers' | 'declare_targets' | 'declare_blockers' | 'resolve';

// Dice roll display state
interface ActiveDiceRoll {
  value: number;
  isReroll: boolean;
  id: number;
}

// Targeting system types
interface TargetingState {
  isTargeting: boolean;
  sourceCardId: string | null;
  sourceAbilityId: string | null;
  targetType: 'creature' | 'player' | 'any' | 'none';
  targetCount: number;
  selectedTargets: string[]; // Can be creature instance IDs or player IDs (as strings)
  onComplete: ((targets: string[]) => void) | null;
}

// Props for multiplayer support
interface CrossroadsProps {
  initialGameState?: GameState;
  onGameStateChange?: (newState: GameState) => void;
  disableActions?: boolean;
  isMultiplayer?: boolean;
  playerIndex?: number; // Which player this user controls (0 or 1)
}

const Crossroads: React.FC<CrossroadsProps> = ({
  initialGameState,
  onGameStateChange,
  disableActions = false,
  isMultiplayer = false,
  playerIndex = 0,
}) => {
  const history = useHistory();
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [combatAttackers, setCombatAttackers] = useState<CombatDeclaration[]>([]);
  const [combatBlockers, setCombatBlockers] = useState<BlockDeclaration[]>([]);
  const [combatStep, setCombatStep] = useState<CombatStep>('declare_attackers');
  const [selectedAttacker, setSelectedAttacker] = useState<string | null>(null);
  const [showCombatHelp, setShowCombatHelp] = useState(false);
  const [showCombatPreview, setShowCombatPreview] = useState(false);
  // Simplified combat system
  const [selectedCombatAttacker, setSelectedCombatAttacker] = useState<string | null>(null);
  const [presentToast] = useIonToast();
  const [activeDiceRolls, setActiveDiceRolls] = useState<ActiveDiceRoll[]>([]);
  const [diceRollIdCounter, setDiceRollIdCounter] = useState(0);

  // AI Configuration
  const [opponentType, setOpponentType] = useState<'human' | 'ai'>('ai');
  const [aiDifficulty, setAIDifficulty] = useState<AIDifficulty>(AIDifficulty.MEDIUM);
  const [isAIPlayer, setIsAIPlayer] = useState<Record<number, boolean>>({});
  const [isExecutingAITurn, setIsExecutingAITurn] = useState(false);

  // Challenge modal
  const [showChallengeModal, setShowChallengeModal] = useState(false);

  // Deck Builder
  const [showDeckBuilder, setShowDeckBuilder] = useState(false);
  const [customP1Deck, setCustomP1Deck] = useState<DeckDefinition | null>(null);
  const [customP2Deck, setCustomP2Deck] = useState<DeckDefinition | null>(null);
  const [deckBuilderFor, setDeckBuilderFor] = useState<'p1' | 'p2'>('p1');

  // Starter Deck Selection
  const [selectedP1StarterDeck, setSelectedP1StarterDeck] = useState<string>('fire');
  const [selectedP2StarterDeck, setSelectedP2StarterDeck] = useState<string>('water');
  const [selectedEnvironmentDeck, setSelectedEnvironmentDeck] = useState<string>('enchanted_forest');

  // Advanced Targeting System
  const [targetingState, setTargetingState] = useState<TargetingState>({
    isTargeting: false,
    sourceCardId: null,
    sourceAbilityId: null,
    targetType: 'none',
    targetCount: 1,
    selectedTargets: [],
    onComplete: null,
  });

  // Tutorial System
  const [showTutorial, setShowTutorial] = useState(false);
  const TUTORIAL_STORAGE_KEY = 'crossroads_tutorial_completed';

  // Game Menu
  const [showGameMenu, setShowGameMenu] = useState(false);
  const [showForfeitAlert, setShowForfeitAlert] = useState(false);
  const [showMulliganPrompt, setShowMulliganPrompt] = useState(false);
  const [hasAskedForMulligan, setHasAskedForMulligan] = useState(false);

  // Card Detail Modal
  const [cardDetailModalOpen, setCardDetailModalOpen] = useState(false);
  const [cardDetailId, setCardDetailId] = useState<string | null>(null);
  const [manaSourceInstanceId, setManaSourceInstanceId] = useState<string | null>(null);
  const [creatureInstanceId, setCreatureInstanceId] = useState<string | null>(null);

  // Initialize game from props in multiplayer mode
  useEffect(() => {
    if (isMultiplayer && initialGameState && !gameState) {
      setGameState(initialGameState);
      // In multiplayer, opponent is always human
      setIsAIPlayer({ 0: false, 1: false });
    }
  }, [isMultiplayer, initialGameState]);

  // Sync state changes to parent in multiplayer mode
  useEffect(() => {
    if (isMultiplayer && gameState && onGameStateChange) {
      onGameStateChange(gameState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, isMultiplayer]);

  // Watch for dice roll events and display indicators
  useEffect(() => {
    if (!gameState?.eventLog) return;

    const lastEvent = gameState.eventLog[gameState.eventLog.length - 1];
    if (!lastEvent || lastEvent.type !== 'dice_roll') return;

    // Parse dice roll from message
    const rollMatch = lastEvent.message.match(/rolled a (\d+)|rerolled \d+ → (\d+)/);
    if (rollMatch) {
      const value = parseInt(rollMatch[1] || rollMatch[2]);
      const isReroll = lastEvent.message.includes('rerolled');

      // Add new dice roll to display
      const newRoll: ActiveDiceRoll = {
        value,
        isReroll,
        id: diceRollIdCounter,
      };

      setActiveDiceRolls(prev => [...prev, newRoll]);
      setDiceRollIdCounter(prev => prev + 1);
    }
  }, [gameState?.eventLog?.length, diceRollIdCounter]);

  // Remove dice roll indicator after animation
  const handleDiceRollComplete = useCallback((id: number) => {
    setActiveDiceRolls(prev => prev.filter(roll => roll.id !== id));
  }, []);

  // Deep clone helper using JSON serialization (safe for game state, omits functions)
  // For GameState, we preserve cardInstances and triggers since they contain effect functions
  const deepClone = useCallback(<T,>(obj: T): T => {
    // Check if this is a GameState object
    if (obj && typeof obj === 'object' && 'cardInstances' in obj) {
      const gameState = obj as any;
      const cardInstances = gameState.cardInstances; // Save reference to cards
      const triggers = gameState.triggers; // Save reference to triggers
      const pendingModalChoice = gameState.pendingModalChoice; // Save modal with functions
      const pendingTargetSelection = gameState.pendingTargetSelection; // Save target selector with functions
      const cloned = JSON.parse(JSON.stringify(obj));
      cloned.cardInstances = cardInstances; // Restore card instances with their functions
      cloned.triggers = triggers; // Restore triggers with their effect functions
      cloned.pendingModalChoice = pendingModalChoice; // Restore modal choice with onComplete/onCancel
      cloned.pendingTargetSelection = pendingTargetSelection; // Restore target selection with onComplete/onCancel/filter
      return cloned as T;
    }
    return JSON.parse(JSON.stringify(obj));
  }, []);

  // Toast notification helper
  const showToast = useCallback((
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    duration: number = 2000
  ) => {
    const colors = {
      success: 'success',
      error: 'danger',
      warning: 'warning',
      info: 'dark',
    };

    const icons = {
      success: '✓ ',
      error: '✗ ',
      warning: '⚠ ',
      info: 'ℹ ',
    };

    presentToast({
      message: `${icons[type]}${message}`,
      duration,
      position: 'top',
      color: colors[type],
    });
  }, [presentToast]);

  // Check if user has completed tutorial before
  useEffect(() => {
    if (!gameState || isMultiplayer) return;

    const hasCompletedTutorial = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    if (!hasCompletedTutorial) {
      // Show tutorial on first game
      setShowTutorial(true);
    }
  }, [gameState, isMultiplayer]);

  // Memoize derived values to prevent unnecessary recalculations
  // These use optional chaining to be safe when gameState is null, but after the early return
  // (line ~1454) we know gameState exists, so these will be defined
  const currentPlayer = useMemo(
    () => gameState?.players[gameState?.currentPlayerIndex],
    [gameState?.players, gameState?.currentPlayerIndex]
  );

  const player = useMemo(
    () => gameState?.players[playerIndex],
    [gameState?.players, playerIndex]
  );

  const opponent = useMemo(
    () => gameState?.players[1 - playerIndex],
    [gameState?.players, playerIndex]
  );

  const commanderCard = useMemo(
    () => player?.commander.cardId ? ALL_CARDS[player.commander.cardId] as CommanderCard : undefined,
    [player?.commander.cardId]
  );

  const opponentCommanderCard = useMemo(
    () => opponent?.commander.cardId ? ALL_CARDS[opponent.commander.cardId] as CommanderCard : undefined,
    [opponent?.commander.cardId]
  );

  // Type assertions - these are guaranteed to be defined after the gameState check
  // We use ! to tell TypeScript these won't be undefined at runtime
  const safeCurrentPlayer = currentPlayer!;
  const safePlayer = player!;
  const safeOpponent = opponent!;
  const safeCommanderCard = commanderCard!;
  const safeOpponentCommanderCard = opponentCommanderCard!;

  // Tutorial completion handler
  const handleTutorialComplete = useCallback(() => {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    setShowTutorial(false);
    showToast('Tutorial completed! Good luck!', 'success', 3000);
  }, [showToast]);

  // Tutorial skip handler
  const handleTutorialSkip = useCallback(() => {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    setShowTutorial(false);
  }, []);

  // Define tutorial steps
  const tutorialSteps: TutorialStep[] = [
    {
      title: 'Welcome to Crossroads TCG!',
      description: 'This tutorial will teach you the basics of the game. You can skip it anytime or navigate back and forth through the steps.',
      position: 'center',
    },
    {
      title: 'Your Commander',
      description: 'This is your Game Master (Commander). They have unique abilities you can activate during your turn. Protect them! If their health reaches 0, you lose.',
      highlightElement: '.player-area .commander-card',
      position: 'top',
    },
    {
      title: 'Opponent Commander',
      description: 'This is your opponent\'s commander. Your goal is to reduce their health to 0 to win the game!',
      highlightElement: '.opponent-area',
      position: 'bottom',
    },
    {
      title: 'Game Phases',
      description: 'Each turn has 5 phases: Opening (draw), Mana (play mana sources), Main (play cards), Conflict (attack), and Resolution (end turn effects). The current phase is always shown.',
      highlightElement: '.game-status',
      position: 'top',
    },
    {
      title: 'Your Hand',
      description: 'These are the cards in your hand. You can play creatures, mana sources, and art cards during the appropriate phases. Click a card to see its details.',
      highlightElement: '.hand-zone',
      position: 'top',
    },
    {
      title: 'Mana Pool',
      description: 'Your mana pool shows how much mana you have available. Play mana source cards during the Mana Phase to generate mana each turn. You need mana to play creatures and cast arts!',
      highlightElement: '.mana-pool',
      position: 'left',
    },
    {
      title: 'Battlefield',
      description: 'This is your battlefield where your creatures are summoned. Creatures can attack during the Conflict Phase and defend your commander from enemy attacks.',
      highlightElement: '.player-area .battlefield',
      position: 'top',
    },
    {
      title: 'Playing Cards',
      description: 'During the Main Phase, you can play creatures and art cards. During the Mana Phase, you can play mana sources. Cards cost mana to play, shown in the top-right corner.',
      position: 'center',
    },
    {
      title: 'Combat Phase',
      description: 'During the Conflict Phase, you can select your creatures to attack. Tap them to declare as attackers, then choose targets (opponent commander or their creatures).',
      position: 'center',
    },
    {
      title: 'Type Advantage',
      description: 'Some element types have advantages over others! For example, Fire beats Wind and Earth. When you attack with type advantage, your creatures get +1 power!',
      position: 'center',
    },
    {
      title: 'Event Log',
      description: 'The event log shows recent game actions like dice rolls, shields added, and creatures defeated. This helps you track what happened during your opponent\'s turn.',
      highlightElement: '.game-event-log',
      position: 'left',
    },
    {
      title: 'Advancing Phases',
      description: 'Use the "Next Phase" or "End Turn" button to advance through phases. When you end your turn, it becomes your opponent\'s turn!',
      highlightElement: '.game-controls',
      position: 'top',
    },
    {
      title: 'Ready to Play!',
      description: 'You\'re all set! Remember: draw cards, play mana sources, summon creatures, attack with them, and reduce your opponent\'s commander to 0 health. Have fun!',
      position: 'center',
    },
  ];

  // Initialize game
  const startGame = useCallback(() => {
    // Use custom decks if available, otherwise use selected starter decks
    const p1Deck = customP1Deck || STARTER_DECKS[selectedP1StarterDeck];
    const p2Deck = customP2Deck || STARTER_DECKS[selectedP2StarterDeck];
    const envDeck = ENVIRONMENT_DECKS[selectedEnvironmentDeck];

    const game = GameLogic.createInitialGameState(
      p1Deck,
      p2Deck,
      envDeck,
      'You',
      opponentType === 'ai' ? `AI (${aiDifficulty})` : 'Opponent'
    );

    // Track which players are AI
    const aiPlayers: Record<number, boolean> = {
      0: false, // Player 0 is always human
      1: opponentType === 'ai', // Player 1 is AI if selected
    };
    setIsAIPlayer(aiPlayers);

    setGameState(game);
    const p1Name = customP1Deck ? customP1Deck.name : STARTER_DECKS[selectedP1StarterDeck].name;
    const p2Name = customP2Deck ? customP2Deck.name : STARTER_DECKS[selectedP2StarterDeck].name;
    const opponentDesc = opponentType === 'ai' ? `AI (${aiDifficulty} difficulty)` : 'Human';
    showToast(`Game Started! ${p1Name} vs ${p2Name} against ${opponentDesc}`, 'success', 3000);
  }, [opponentType, aiDifficulty, customP1Deck, customP2Deck, selectedP1StarterDeck, selectedP2StarterDeck, selectedEnvironmentDeck, showToast]);

  // Improved AI: Auto-play opponent's turn with strategy
  React.useEffect(() => {
    if (!gameState) return;

    // DISABLED: This simple AI has been replaced by the advanced AI system (executeAITurn)
    // The advanced AI supports commander abilities, mulligan, and strategic play
    // Keeping this code for reference but it should not run
    return;

    /* eslint-disable */
    // OLD SIMPLE AI CODE - DISABLED - DO NOT USE
    /*
    // Check if it's the opponent's turn (player index 1)
    if (gameState.currentPlayerIndex === 1) {
      const timer = setTimeout(() => {
        const newGame = deepClone(gameState);
        const aiPlayer = newGame.players[1];

        // Make AI decisions based on current phase
        if (newGame.currentPhase === Phase.RISING_ACTION) {
          // 1. Play a mana source if available
          if (!aiPlayer.hasPlayedManaThisTurn) {
            const manaSourceInHand = aiPlayer.hand.find(
              (cardId: string) => newGame.cardInstances[cardId]?.type === CardType.MANA_SOURCE
            );
            if (manaSourceInHand) {
              GameLogic.playManaSource(newGame, 1, manaSourceInHand);
              showToast('AI played a mana source');
            }
          }

          // 2. Try to play creatures from hand
          const creaturesInHand = aiPlayer.hand.filter(
            (cardId: string) => newGame.cardInstances[cardId]?.type === CardType.CREATURE
          );
          for (const creatureId of creaturesInHand) {
            const creature = newGame.cardInstances[creatureId] as CreatureCard;
            if (creature && GameLogic.canAffordCost(aiPlayer.manaPool, creature.cost)) {
              const success = GameLogic.playCreature(newGame, 1, creatureId);
              if (success) {
                showToast(`AI played ${creature.name}`);
              }
            }
          }

          // 3. Try to play art spells
          const artsInHand = aiPlayer.hand.filter(
            (cardId: string) => newGame.cardInstances[cardId]?.type === CardType.ART
          );
          for (const artId of artsInHand) {
            const art = newGame.cardInstances[artId] as ArtCard;
            if (art && GameLogic.canAffordCost(aiPlayer.manaPool, art.cost)) {
              // For now, skip spells that require targeting
              // Simple AI just plays non-targeted spells
              if (!art.targetType || art.targetType === 'none') {
                const success = GameLogic.playArt(newGame, 1, artId);
                if (success) {
                  showToast(`AI cast ${art.name}`);
                }
              }
            }
          }
        } else if (newGame.currentPhase === Phase.CONFLICT) {
          // Attack with all available creatures
          const attackers: CombatDeclaration[] = aiPlayer.battlefield.creatures
            .filter((c: BattlefieldCreature) => !c.isTapped && !c.summoningSick)
            .map((c: BattlefieldCreature) => ({
              attackerId: c.instanceId,
              targetId: '0', // Attack player 0 (human)
              targetType: 'player' as const,
            }));

          if (attackers.length > 0) {
            GameLogic.declareAttackers(newGame, 1, attackers);
            GameLogic.resolveCombat(newGame, [], []); // No blockers for now
            showToast(`AI attacks with ${attackers.length} creature(s)!`);
          }
        }

        // Advance to next phase
        GameLogic.advancePhase(newGame);
        setGameState(newGame);

        // Reset combat state when exiting conflict phase
        if (newGame.currentPhase === Phase.RESOLUTION) {
          setCombatAttackers([]);
          setCombatBlockers([]);
          setCombatStep('declare_attackers');
          setSelectedAttacker(null);
        }
      }, 1500); // 1.5 second delay for each phase

      return () => clearTimeout(timer);
    }
    */
  }, [gameState?.currentPhase, gameState?.currentPlayerIndex]);

  // ============================================================================
  // AI MODAL/TARGET AUTO-HANDLING
  // ============================================================================

  // Automatically handle modals and target selections for AI players
  useEffect(() => {
    if (!gameState) return;

    // Auto-handle modal choices for AI
    if (gameState.pendingModalChoice) {
      const modal = gameState.pendingModalChoice;
      if (isAIPlayer[modal.playerId]) {
        // AI should auto-select:
        // - For binary/choose-one: pick first option
        // - For reveal info: just close it
        setTimeout(() => {
          if (modal.type === ModalChoiceType.REVEAL_INFO && modal.onComplete) {
            modal.onComplete([]);
          } else if (modal.options && modal.options.length > 0 && modal.onComplete) {
            modal.onComplete([modal.options[0].id]);
          }
          const newGame = { ...gameState };
          setGameState(newGame);
        }, 100);
      }
    }

    // Auto-handle target selections for AI
    if (gameState.pendingTargetSelection) {
      const targetReq = gameState.pendingTargetSelection;
      if (isAIPlayer[targetReq.playerId]) {
        // AI should auto-cancel targeting
        setTimeout(() => {
          if (targetReq.onCancel) {
            targetReq.onCancel();
          } else if (targetReq.onComplete) {
            targetReq.onComplete({ creatures: [], players: [] });
          }
          const newGame = { ...gameState };
          setGameState(newGame);
        }, 100);
      }
    }
  }, [gameState?.pendingModalChoice, gameState?.pendingTargetSelection, isAIPlayer]);

  // ============================================================================
  // MULLIGAN PROMPT
  // ============================================================================

  // Automatically prompt for mulligan at start of game
  useEffect(() => {
    if (!gameState || hasAskedForMulligan || isMultiplayer) return;

    // Show mulligan prompt at start of Round 1, OPENING phase, for player 0
    if (gameState.currentRound === 1 &&
        gameState.currentPhase === Phase.OPENING &&
        gameState.currentPlayerIndex === 0 &&
        !gameState.gameOver) {
      const player = gameState.players[0];

      // Only if player hasn't taken any actions yet
      if (!player.hasPlayedManaThisTurn &&
          player.battlefield.creatures.length === 0 &&
          player.battlefield.manaSources.length === 0 &&
          player.hand.length > 0) {

        // Check if player has any BASIC mana source cards in hand (no cost to play)
        const hasBasicManaSource = player.hand.some(cardId => {
          const card = gameState.cardInstances[cardId];
          if (!card || card.type !== CardType.MANA_SOURCE) return false;
          const manaCard = card as any;
          // Basic mana sources have no cost (or empty cost object)
          return !manaCard.cost || Object.keys(manaCard.cost).length === 0;
        });

        // Only show mulligan if they have NO basic mana sources
        if (!hasBasicManaSource) {
          setShowMulliganPrompt(true);
          setHasAskedForMulligan(true);
        } else {
          // They have basic mana, mark as asked so we don't check again
          setHasAskedForMulligan(true);
        }
      }
    }
  }, [gameState, hasAskedForMulligan, isMultiplayer]);

  // ============================================================================
  // AI TURN EXECUTION
  // ============================================================================

  // Automatically execute AI turns
  useEffect(() => {
    if (!gameState || gameState.gameOver || isExecutingAITurn) return;

    // ENVIRONMENT phase should auto-advance immediately (no player actions)
    if (gameState.currentPhase === Phase.ENVIRONMENT) {
      setIsExecutingAITurn(true);
      setTimeout(() => {
        const newGame = deepClone(gameState);
        GameLogic.advancePhase(newGame);
        setGameState(newGame);
        setIsExecutingAITurn(false);
      }, 500); // Small delay for UX
      return;
    }

    const currentPlayerId = gameState.currentPlayerIndex;
    const isAI = isAIPlayer[currentPlayerId];

    // Handle AI combat phase separately
    if (isAI && gameState.currentPhase === Phase.CONFLICT) {
      setIsExecutingAITurn(true);

      setTimeout(() => {
        const newGame = deepClone(gameState);
        const aiPlayer = newGame.players[currentPlayerId];

        // Declare all available creatures as attackers
        const attackers: CombatDeclaration[] = aiPlayer.battlefield.creatures
          .filter((c: BattlefieldCreature) => !c.isTapped && !c.summoningSick)
          .map((c: BattlefieldCreature) => ({
            attackerId: c.instanceId,
            targetId: currentPlayerId === 0 ? '1' : '0', // Attack the other player
            targetType: 'player' as const,
          }));

        if (attackers.length > 0) {
          GameLogic.declareAttackers(newGame, currentPlayerId, attackers);
          showToast(`AI attacks with ${attackers.length} creature(s)!`);
        }

        // Advance phase
        GameLogic.advancePhase(newGame);
        setGameState(newGame);
        setIsExecutingAITurn(false);
      }, 1000);
      return;
    }

    // If current player is AI and we're in a phase where AI can act
    if (isAI && gameState.currentPhase !== Phase.CONFLICT) {
      setIsExecutingAITurn(true);

      // Small delay for better UX
      setTimeout(async () => {
        try {
          showToast(`AI ${aiDifficulty} is thinking...`);
          const updatedGame = await executeAITurn(gameState, currentPlayerId, aiDifficulty);
          setGameState(updatedGame);

          // Advance to next phase after AI completes their turn
          setTimeout(() => {
            const finalGame = { ...updatedGame };
            GameLogic.advancePhase(finalGame);
            setGameState(finalGame);
            setIsExecutingAITurn(false);
          }, 500);
        } catch (error) {
          logger.error('AI turn execution error:', error);
          showToast('AI turn failed');
          setIsExecutingAITurn(false);
        }
      }, 1000);
    }
  }, [gameState, isAIPlayer, aiDifficulty, isExecutingAITurn, deepClone]);

  // ============================================================================
  // ADVANCED TARGETING SYSTEM
  // ============================================================================

  // Start targeting mode for a card/ability
  const startTargeting = useCallback(
    (
      sourceCardId: string,
      targetType: 'creature' | 'player' | 'any' | 'none',
      targetCount: number,
      onComplete: (targets: string[]) => void,
      sourceAbilityId?: string
    ) => {
      if (targetType === 'none' || targetCount === 0) {
        // No targeting needed, execute immediately
        onComplete([]);
        return;
      }

      setTargetingState({
        isTargeting: true,
        sourceCardId,
        sourceAbilityId: sourceAbilityId || null,
        targetType,
        targetCount,
        selectedTargets: [],
        onComplete,
      });

      showToast(`Select ${targetCount} ${targetType}${targetCount > 1 ? 's' : ''}`);
    },
    []
  );

  // Handle clicking on a potential target
  const handleTargetClick = useCallback(
    (targetId: string, targetIsPlayer: boolean) => {
      if (!targetingState.isTargeting) return;

      const { targetType, targetCount, selectedTargets, onComplete } = targetingState;

      // Validate target type
      if (targetType === 'player' && !targetIsPlayer) {
        showToast('Must target a player');
        return;
      }
      if (targetType === 'creature' && targetIsPlayer) {
        showToast('Must target a creature');
        return;
      }

      // Toggle selection
      const newTargets = selectedTargets.includes(targetId)
        ? selectedTargets.filter((t) => t !== targetId)
        : [...selectedTargets, targetId];

      // Check if we have enough targets
      if (newTargets.length > targetCount) {
        showToast(`Can only select ${targetCount} target${targetCount > 1 ? 's' : ''}`);
        return;
      }

      setTargetingState((prev) => ({
        ...prev,
        selectedTargets: newTargets,
      }));

      // Auto-complete if we've selected enough targets
      if (newTargets.length === targetCount) {
        onComplete?.(newTargets);
        setTargetingState({
          isTargeting: false,
          sourceCardId: null,
          sourceAbilityId: null,
          targetType: 'none',
          targetCount: 1,
          selectedTargets: [],
          onComplete: null,
        });
      }
    },
    [targetingState]
  );

  // Cancel targeting
  const cancelTargeting = useCallback(() => {
    setTargetingState({
      isTargeting: false,
      sourceCardId: null,
      sourceAbilityId: null,
      targetType: 'none',
      targetCount: 1,
      selectedTargets: [],
      onComplete: null,
    });
    showToast('Targeting cancelled');
  }, [showToast]);

  // Check if a target is valid based on current targeting state
  const isValidTarget = useCallback(
    (targetId: string, isPlayer: boolean): boolean => {
      if (!targetingState.isTargeting) return false;

      const { targetType } = targetingState;

      if (targetType === 'none') return false;
      if (targetType === 'player') return isPlayer;
      if (targetType === 'creature') return !isPlayer;
      if (targetType === 'any') return true;

      return false;
    },
    [targetingState]
  );

  // ============================================================================
  // END TARGETING SYSTEM
  // ============================================================================

  // Helper to check if current player can take actions
  const canTakeAction = useCallback((): boolean => {
    if (!gameState) return false;
    if (disableActions) return false;

    // In multiplayer mode, check if it's this player's turn
    if (isMultiplayer) {
      return gameState.currentPlayerIndex === playerIndex;
    }

    // In single-player mode, only player 0 (human) can take actions
    return gameState.currentPlayerIndex === 0;
  }, [gameState, disableActions, isMultiplayer, playerIndex]);

  // Play a creature from hand
  const handlePlayCreature = useCallback(
    (cardId: string) => {
      if (!gameState) return;

      if (gameState.gameOver) {
        showToast('Game is over! Start a new game.', 'warning');
        return;
      }

      if (!canTakeAction()) {
        showToast('Not your turn! Wait for your opponent.', 'warning');
        return;
      }

      if (gameState.currentPhase !== Phase.RISING_ACTION) {
        showToast('Can only play creatures during Rising Action phase', 'warning');
        return;
      }

      const newGame = deepClone(gameState);
      const success = GameLogic.playCreature(newGame, playerIndex, cardId);

      if (success) {
        const card = ALL_CARDS[cardId];
        setGameState(newGame);
        setSelectedCard(null);
        showToast(`${card.name} summoned to the battlefield!`, 'success');
      } else {
        showToast('Cannot play this creature (not enough mana?)', 'error');
      }
    },
    [gameState, canTakeAction, playerIndex]
  );

  // Play a mana source
  const handlePlayManaSource = useCallback(
    (cardId: string) => {
      if (!gameState) return;

      if (!canTakeAction()) {
        showToast('Not your turn! Wait for your opponent.', 'warning');
        return;
      }

      if (gameState.currentPhase !== Phase.RISING_ACTION) {
        showToast('Can only play mana sources during Rising Action phase', 'warning');
        return;
      }

      const player = gameState.players[playerIndex];
      const card = gameState.cardInstances[cardId];

      // Check specific failure reasons before attempting to play
      if (!card || card.type !== CardType.MANA_SOURCE) {
        showToast('Invalid card', 'error');
        return;
      }

      const manaSourceCard = card as ManaSourceCard;

      if (!player.hand.includes(cardId)) {
        showToast('Card not in hand', 'error');
        return;
      }

      if (player.hasPlayedManaThisTurn) {
        showToast('Already played a mana source this turn', 'warning');
        return;
      }

      // Check if mana source has a cost and if player can afford it
      if (manaSourceCard.cost && Object.keys(manaSourceCard.cost).length > 0) {
        if (!GameLogic.canAffordCost(player.manaPool, manaSourceCard.cost)) {
          showToast('Not enough mana to play this mana source', 'error');
          return;
        }
      }

      const newGame = deepClone(gameState);
      const success = GameLogic.playManaSource(newGame, playerIndex, cardId);

      if (success) {
        const card = ALL_CARDS[cardId];
        setGameState(newGame);
        setSelectedCard(null);
        showToast(`${card.name} played! Mana increased.`, 'success');
      } else {
        showToast('Cannot play mana source', 'error');
      }
    },
    [gameState, canTakeAction, playerIndex]
  );

  // Play an Art card (with targeting support)
  const handlePlayArt = useCallback(
    (cardId: string) => {
      if (!gameState) return;

      if (!canTakeAction()) {
        showToast('Not your turn! Wait for your opponent.', 'warning');
        return;
      }

      if (gameState.currentPhase !== Phase.RISING_ACTION) {
        showToast('Can only play Arts during Rising Action phase', 'warning');
        return;
      }

      const card = ALL_CARDS[cardId] as ArtCard;
      if (!card || card.type !== CardType.ART) return;

      // Check if card requires targeting
      if (card.targetType && card.targetType !== 'none' && card.targetCount) {
        // Close the card detail modal so targeting UI is visible
        setCardDetailModalOpen(false);
        setCardDetailId(null);

        startTargeting(cardId, card.targetType, card.targetCount, (targets) => {
          const newGame = deepClone(gameState);
          const success = GameLogic.playArt(newGame, playerIndex, cardId, {
            creatures: targets.filter((t) => !t.startsWith('player_')),
            players: targets.filter((t) => t.startsWith('player_')).map((t) => parseInt(t.replace('player_', ''))),
          });

          if (success) {
            const card = ALL_CARDS[cardId];
            setGameState(newGame);
            setSelectedCard(null);
            showToast(`${card.name} cast successfully!`, 'success');
          } else {
            showToast('Cannot play this Art', 'error');
          }
        });
      } else {
        // No targeting needed
        const newGame = deepClone(gameState);
        const success = GameLogic.playArt(newGame, playerIndex, cardId);

        if (success) {
          setGameState(newGame);
          setSelectedCard(null);
          showToast('Art played!');
        } else {
          showToast('Cannot play this Art (not enough mana?)');
        }
      }
    },
    [gameState, startTargeting, canTakeAction, playerIndex]
  );

  // Activate Commander Ability
  const handleActivateCommanderAbility = useCallback(
    (abilityId: string) => {
      if (!gameState || !commanderCard) return;

      if (gameState.gameOver) {
        showToast('Game is over! Start a new game.', 'warning');
        return;
      }

      if (!canTakeAction()) {
        showToast('Not your turn! Wait for your opponent.', 'warning');
        return;
      }

      const newGame = deepClone(gameState);
      const success = GameLogic.activateCommanderAbility(newGame, playerIndex, abilityId);

      if (success) {
        setGameState(newGame);
        const ability = commanderCard.abilities.find((a: CommanderAbility) => a.id === abilityId);
        showToast(`${ability?.description || 'Ability'} activated!`, 'success');
      } else {
        showToast('Cannot activate ability (not enough mana or already used?)', 'error');
      }
    },
    [gameState, canTakeAction, playerIndex, commanderCard]
  );

  // Send chat message
  const handleSendMessage = useCallback(
    (message: string) => {
      if (!gameState) return;

      const newGame = deepClone(gameState);
      const playerName = newGame.players[playerIndex].name;
      GameLogic.sendChatMessage(newGame, playerIndex, playerName, message);
      setGameState(newGame);
    },
    [gameState, playerIndex]
  );

  // Play card from detail modal
  const handlePlayCardFromModal = useCallback(() => {
    if (!cardDetailId || !gameState) return;

    const card = ALL_CARDS[cardDetailId];
    if (!card) return;

    // Call the appropriate handler based on card type
    if (card.type === CardType.CREATURE) {
      handlePlayCreature(cardDetailId);
    } else if (card.type === CardType.MANA_SOURCE) {
      handlePlayManaSource(cardDetailId);
    } else if (card.type === CardType.ART) {
      handlePlayArt(cardDetailId);
    } else {
      showToast('Not implemented yet');
    }
  }, [cardDetailId, gameState, handlePlayCreature, handlePlayManaSource, handlePlayArt]);

  // Advance to next phase
  const handleNextPhase = useCallback(() => {
    if (!gameState) return;

    // Prevent actions if game is over
    if (gameState.gameOver) {
      showToast('Game is over! Start a new game.', 'warning');
      return;
    }

    if (!canTakeAction()) {
      showToast('Not your turn! Wait for your opponent.', 'warning');
      return;
    }

    const newGame = deepClone(gameState);
    GameLogic.advancePhase(newGame);
    setGameState(newGame);
    showToast(`Advancing to ${newGame.currentPhase}`, 'info');

    // Reset combat state when exiting conflict phase
    if (newGame.currentPhase === Phase.RESOLUTION) {
      setCombatAttackers([]);
      setCombatBlockers([]);
      setCombatStep('declare_attackers');
      setSelectedAttacker(null);
    }
  }, [gameState, canTakeAction]);

  // Discard hand and redraw - skips the turn
  const handleDiscardAndRedraw = useCallback(() => {
    if (!gameState) return;

    if (!canTakeAction()) {
      showToast('Not your turn! Wait for your opponent.', 'warning');
      return;
    }

    // Can only mulligan during OPENING phase before taking any actions
    if (gameState.currentPhase !== Phase.OPENING) {
      showToast('Mulligan can only be used during the Opening phase!', 'warning');
      return;
    }

    const playerCheck = gameState.players[0];

    // Check if player has taken any actions this turn
    if (playerCheck.hasPlayedManaThisTurn ||
        playerCheck.battlefield.creatures.length > 0 ||
        playerCheck.battlefield.manaSources.length > 0) {
      showToast('Mulligan can only be used BEFORE taking any other actions!', 'warning');
      return;
    }

    const newGame = deepClone(gameState);
    const player = newGame.players[0];

    // Discard hand into deck and shuffle
    player.deck.push(...player.hand);
    player.hand = [];
    player.deck = shuffleArray(player.deck);

    // Draw 7 new cards
    GameLogic.drawCards(newGame, 0, 7);

    // Log the action
    GameLogic.logEvent(newGame, 'ability_activated', `${player.name} discarded their hand and drew 7 new cards, skipping their turn`);

    // Advance to end of turn (Resolution phase)
    while (newGame.currentPhase !== Phase.RESOLUTION) {
      GameLogic.advancePhase(newGame);
    }

    // Reset combat state
    setCombatAttackers([]);
    setCombatBlockers([]);
    setCombatStep('declare_attackers');
    setSelectedAttacker(null);

    setGameState(newGame);
    showToast('Hand discarded and redrawn. Turn skipped.', 'warning');
  }, [gameState, canTakeAction]);

  // Combat: Toggle attacker
  const handleToggleAttacker = useCallback(
    (creatureId: string) => {
      if (!gameState) return;
      if (gameState.currentPhase !== Phase.CONFLICT) return;
      if (combatStep !== 'declare_attackers') return;

      if (!canTakeAction()) {
        showToast('Not your turn!');
        return;
      }

      const player = gameState.players[playerIndex];
      const creature = player.battlefield.creatures.find((c: BattlefieldCreature) => c.instanceId === creatureId);

      if (!creature) return;
      if (creature.isTapped || creature.summoningSick) {
        showToast('This creature cannot attack!');
        return;
      }

      // Check if already attacking
      const existingIndex = combatAttackers.findIndex((a) => a.attackerId === creatureId);

      if (existingIndex !== -1) {
        // Remove from attackers
        setCombatAttackers(combatAttackers.filter((a) => a.attackerId !== creatureId));
      } else {
        // Add to attackers (target will be assigned next step)
        setCombatAttackers([
          ...combatAttackers,
          {
            attackerId: creatureId,
            targetId: '1', // Default to opponent player
            targetType: 'player',
          },
        ]);
      }
    },
    [gameState, combatAttackers, combatStep, canTakeAction, playerIndex]
  );

  // Combat: Confirm attackers and move to targeting
  const handleConfirmAttackers = useCallback(() => {
    if (!canTakeAction()) {
      showToast('Not your turn!');
      return;
    }
    if (combatAttackers.length === 0) {
      showToast('No attackers selected');
      return;
    }
    setCombatStep('declare_targets');
    setSelectedAttacker(combatAttackers[0].attackerId);
  }, [combatAttackers, canTakeAction]);

  // Combat: Assign target for an attacker
  const handleAssignTarget = useCallback(
    (attackerId: string, targetId: string, targetType: 'creature' | 'player') => {
      setCombatAttackers(
        combatAttackers.map((attack) =>
          attack.attackerId === attackerId
            ? { ...attack, targetId, targetType }
            : attack
        )
      );
    },
    [combatAttackers]
  );

  // Combat: Skip attackers (do nothing in combat)
  const handleSkipCombat = useCallback(() => {
    if (!canTakeAction()) {
      showToast('Not your turn!');
      return;
    }
    setCombatAttackers([]);
    setCombatStep('declare_attackers');
    setSelectedAttacker(null);
    handleNextPhase();
  }, [handleNextPhase, canTakeAction]);

  // Combat: Calculate AI blockers for preview
  const calculatePreviewBlockers = useCallback((): BlockDeclaration[] => {
    if (!gameState) return [];

    const opponentIndex = 1 - playerIndex;
    if (!isAIPlayer[opponentIndex]) {
      // In multiplayer, blockers will be declared by opponent later
      return [];
    }

    // Calculate AI blockers for preview
    const attackingCreatures = combatAttackers
      .map((attack) => {
        return gameState.players[playerIndex].battlefield.creatures.find(
          (c) => c.instanceId === attack.attackerId
        );
      })
      .filter((c): c is BattlefieldCreature => c !== undefined);

    const blockingMap = getAIBlockDecision(
      gameState,
      opponentIndex,
      attackingCreatures,
      aiDifficulty
    );

    return Object.entries(blockingMap).map(([attackerId, blockerId]) => ({
      attackerId,
      blockerId,
    }));
  }, [gameState, playerIndex, isAIPlayer, combatAttackers, aiDifficulty]);

  // Combat: Show combat preview before resolving
  const handleShowCombatPreview = useCallback(() => {
    logger.log('🎯 handleShowCombatPreview called');
    logger.log('Combat attackers:', combatAttackers);
    if (!gameState) return;

    if (!canTakeAction()) {
      showToast('Not your turn!');
      return;
    }

    // Calculate blockers for preview
    const previewBlockers = calculatePreviewBlockers();
    logger.log('Preview blockers calculated:', previewBlockers);
    setCombatBlockers(previewBlockers);

    logger.log('Setting showCombatPreview to true');
    setShowCombatPreview(true);
  }, [gameState, canTakeAction, calculatePreviewBlockers, combatAttackers]);

  // Combat: Resolve combat
  const handleResolveCombat = useCallback(() => {
    logger.log('⚔️ handleResolveCombat called');
    if (!gameState) return;

    if (!canTakeAction()) {
      showToast('Not your turn!');
      return;
    }

    // Close the preview
    setShowCombatPreview(false);

    const newGame = deepClone(gameState);

    // Declare attackers
    logger.log('Declaring attackers:', combatAttackers);
    const success = GameLogic.declareAttackers(newGame, playerIndex, combatAttackers);

    if (!success) {
      showToast('Failed to declare attackers');
      logger.error('Failed to declare attackers');
      return;
    }

    // AI declares blockers (if opponent is AI)
    let aiBlockers: BlockDeclaration[] = [];
    const opponentIndex = 1 - playerIndex; // The other player
    if (isAIPlayer[opponentIndex]) {
      const attackingCreatures = combatAttackers
        .map((attack) => {
          return newGame.players[playerIndex].battlefield.creatures.find(
            (c) => c.instanceId === attack.attackerId
          );
        })
        .filter((c): c is BattlefieldCreature => c !== undefined);

      const blockingMap = getAIBlockDecision(
        newGame,
        opponentIndex,
        attackingCreatures,
        aiDifficulty
      );

      aiBlockers = Object.entries(blockingMap).map(([attackerId, blockerId]) => ({
        attackerId,
        blockerId,
      }));

      logger.log('AI blockers:', aiBlockers);
      if (aiBlockers.length > 0) {
        showToast(`AI declares ${aiBlockers.length} blocker(s)`);
      } else {
        logger.log('No AI blockers declared');
      }
    }

    // Resolve combat
    logger.log('Resolving combat with attackers:', combatAttackers, 'and blockers:', aiBlockers);
    GameLogic.resolveCombat(newGame, combatAttackers, aiBlockers);

    setGameState(newGame);
    setCombatAttackers([]);
    setCombatBlockers([]);
    setCombatStep('declare_attackers');
    setSelectedAttacker(null);
    showToast('Combat resolved! Damage dealt.', 'success', 3000);

    // Check if game is over
    if (newGame.gameOver) {
      return;
    }

    // Move to resolution phase
    GameLogic.advancePhase(newGame);
    setGameState(newGame);
  }, [gameState, combatAttackers, isAIPlayer, aiDifficulty, canTakeAction, playerIndex]);

  // Simplified Combat: Select attacker
  const handleSelectCombatAttacker = useCallback((creatureId: string) => {
    if (!gameState) return;
    if (gameState.currentPhase !== Phase.CONFLICT) return;

    if (!canTakeAction()) {
      showToast('Not your turn!');
      return;
    }

    const player = gameState.players[playerIndex];
    const creature = player.battlefield.creatures.find((c: BattlefieldCreature) => c.instanceId === creatureId);

    if (!creature) return;
    if (creature.isTapped || creature.summoningSick) {
      showToast('This creature cannot attack!');
      return;
    }

    setSelectedCombatAttacker(creatureId);
    const card = ALL_CARDS[creature.cardId] as CreatureCard;
    showToast(`${card.name} selected - click a target to attack!`, 'info');
  }, [gameState, canTakeAction, playerIndex]);

  // Simplified Combat: Attack target (creature or player)
  const handleCombatTarget = useCallback((targetType: 'creature' | 'player', targetId: string) => {
    if (!gameState || !selectedCombatAttacker) return;

    if (!canTakeAction()) {
      showToast('Not your turn!');
      return;
    }

    const newGame = deepClone(gameState);

    // Resolve the attack immediately
    const success = GameLogic.resolveSingleAttack(
      newGame,
      selectedCombatAttacker,
      targetType,
      targetId
    );

    if (success) {
      setGameState(newGame);
      setSelectedCombatAttacker(null);
    } else {
      showToast('Attack failed!', 'error');
    }
  }, [gameState, selectedCombatAttacker, canTakeAction]);

  // Get element icon
  const getElementIcon = (element: ElementType) => {
    switch (element) {
      case ElementType.FIRE:
        return flameOutline;
      case ElementType.WATER:
        return waterOutline;
      default:
        return flash;
    }
  };

  // Get element color
  const getElementColor = (element: ElementType) => {
    switch (element) {
      case ElementType.FIRE:
        return '#ff4444';
      case ElementType.WATER:
        return '#4488ff';
      default:
        return '#888888';
    }
  };

  // Check if a card can be played in the current phase
  const canPlayCard = (cardId: string): boolean => {
    const card = ALL_CARDS[cardId];
    if (!card || !gameState) return false;

    const currentPlayer = gameState.players[0];
    const currentPhase = gameState.currentPhase;

    // Can't play cards if not your turn
    if (gameState.currentPlayerIndex !== 0) return false;

    // Phase restrictions
    switch (card.type) {
      case CardType.MANA_SOURCE:
        // Can only play mana sources in RISING_ACTION phase
        if (currentPhase !== Phase.RISING_ACTION) return false;
        // Check if already played a mana source this turn
        if (currentPlayer.hasPlayedManaThisTurn) return false;
        // Check if can afford the cost (for non-basic mana sources)
        const manaSourceCard = card as ManaSourceCard;
        if (manaSourceCard.cost && Object.keys(manaSourceCard.cost).length > 0) {
          if (!GameLogic.canAffordCost(currentPlayer.manaPool, manaSourceCard.cost)) return false;
        }
        break;

      case CardType.CREATURE:
        // Can only play creatures in RISING_ACTION phase
        if (currentPhase !== Phase.RISING_ACTION) return false;
        // Check if can afford the cost
        const creatureCard = card as CreatureCard;
        if (!GameLogic.canAffordCost(currentPlayer.manaPool, creatureCard.cost)) return false;
        break;

      case CardType.ART:
        // Can play arts in RISING_ACTION or RESOLUTION phases
        if (currentPhase !== Phase.RISING_ACTION && currentPhase !== Phase.RESOLUTION) return false;
        // Check if can afford the cost
        const artCard = card as ArtCard;
        if (!GameLogic.canAffordCost(currentPlayer.manaPool, artCard.cost)) return false;
        break;

      default:
        // Other card types can't be played from hand
        return false;
    }

    return true;
  };

  // Render card
  const renderCard = (cardId: string, onClick?: () => void, index?: number) => {
    const card = ALL_CARDS[cardId];
    if (!card) return null;

    const isPlayable = canPlayCard(cardId);

    // Use index if provided to ensure unique keys for duplicate cards in hand
    const uniqueKey = index !== undefined ? `${cardId}-${index}` : cardId;

    return (
      <CardTooltip card={card} key={uniqueKey}>
        <div
          className={`game-card ${selectedCard === cardId ? 'selected' : ''}`}
          style={{ display: 'inline-block', position: 'relative' }}
        >
          <CardDisplay
            card={card}
            size="medium"
            onClick={onClick}
            selected={selectedCard === cardId}
            disabled={!isPlayable}
            showDetails={true}
            lazy={false}
          />
        <div style={{
          padding: '8px',
          background: 'rgba(0, 0, 0, 0.8)',
          borderRadius: '0 0 12px 12px',
          marginTop: '-4px'
        }}>
          {card.type === CardType.CREATURE && (() => {
            const creature = card as CreatureCard;
            return (
              <>
                <div className="card-cost" style={{ display: 'flex', gap: '4px', marginBottom: '4px', flexWrap: 'wrap' }}>
                  {Object.entries(creature.cost).map(([type, amount]: [string, number]) => (
                    <IonBadge key={type} color="medium">
                      <IonIcon icon={getElementIcon(type as ElementType)} /> {amount}
                    </IonBadge>
                  ))}
                </div>
                {creature.flavorText && (
                  <IonText className="flavor-text">
                    <p style={{ fontSize: '0.7rem', fontStyle: 'italic', margin: 0, color: 'rgba(255, 255, 255, 0.7)' }}>
                      {creature.flavorText}
                    </p>
                  </IonText>
                )}
              </>
            );
          })()}
          {card.type === CardType.MANA_SOURCE && (() => {
            const manaSource = card as ManaSourceCard;
            return (
              <>
                <IonBadge color="primary">
                  <IonIcon icon={getElementIcon(manaSource.elementType)} /> Mana Source
                </IonBadge>
                {manaSource.flavorText && (
                  <IonText className="flavor-text">
                    <p style={{ fontSize: '0.7rem', fontStyle: 'italic', margin: '4px 0 0 0', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {manaSource.flavorText}
                    </p>
                  </IonText>
                )}
              </>
            );
          })()}
          {card.type === CardType.ART && (() => {
            const art = card as ArtCard;
            return (
              <>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <IonBadge color="warning">Art</IonBadge>
                  <div className="card-cost" style={{ display: 'flex', gap: '4px' }}>
                    {Object.entries(art.cost).map(([type, amount]: [string, number]) => (
                      <IonBadge key={type} color="medium">
                        <IonIcon icon={getElementIcon(type as ElementType)} /> {amount}
                      </IonBadge>
                    ))}
                  </div>
                </div>
                {art.flavorText && (
                  <IonText className="flavor-text">
                    <p style={{ fontSize: '0.7rem', fontStyle: 'italic', margin: 0, color: 'rgba(255, 255, 255, 0.7)' }}>
                      {art.flavorText}
                    </p>
                  </IonText>
                )}
              </>
            );
          })()}
        </div>
        </div>
      </CardTooltip>
    );
  };

  // Render battlefield creature
  const renderBattlefieldCreature = (
    creature: BattlefieldCreature,
    isPlayerCreature: boolean = true,
    onClick?: () => void
  ) => {
    const card = ALL_CARDS[creature.cardId] as CreatureCard;
    if (!card) return null;

    const isAttacking = combatAttackers.some((a) => a.attackerId === creature.instanceId);
    const isTargeted = combatAttackers.some((a) => a.targetId === creature.instanceId);
    const canAttack =
      isPlayerCreature &&
      !creature.isTapped &&
      !creature.summoningSick &&
      gameState?.currentPhase === Phase.CONFLICT &&
      combatStep === 'declare_attackers';

    // Check if this creature can be targeted
    const canBeTargeted = isValidTarget(creature.instanceId, false);
    const isSelectedTarget = targetingState.selectedTargets.includes(creature.instanceId);

    // Calculate type advantage for visual indication
    let hasAdvantage = false;
    if (isAttacking && gameState) {
      const opponent = gameState.players[1];
      const attack = combatAttackers.find((a) => a.attackerId === creature.instanceId);
      if (attack && attack.targetType === 'creature') {
        const targetCreature = opponent.battlefield.creatures.find(
          (c) => c.instanceId === attack.targetId
        );
        if (targetCreature) {
          const targetCard = ALL_CARDS[targetCreature.cardId] as CreatureCard;
          hasAdvantage = hasTypeAdvantage(card.elementType, targetCard.elementType);
        }
      }
    }

    // Determine click handler
    const handleClick = canBeTargeted
      ? () => handleTargetClick(creature.instanceId, false)
      : onClick;

    return (
      <div
        key={creature.instanceId}
        className={`battlefield-creature ${creature.isTapped ? 'tapped' : ''} ${
          isAttacking ? 'attacking' : ''
        } ${isTargeted ? 'targeted' : ''} ${canAttack ? 'can-attack' : ''} ${
          hasAdvantage ? 'type-advantage' : ''
        } ${canBeTargeted ? 'targetable' : ''} ${isSelectedTarget ? 'selected-target' : ''}`}
        style={{ position: 'relative', display: 'inline-block' }}
      >
        <CardDisplay
          card={card}
          size="medium"
          onClick={handleClick}
          selected={isSelectedTarget || isAttacking}
          disabled={false}
          showDetails={false}
          isToken={(creature as any).isToken || false}
        />
        <div style={{
          position: 'absolute',
          bottom: '8px',
          left: '8px',
          right: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          alignItems: 'center'
        }}>
          <div className="creature-stats" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {/* Show current power/health stats */}
            {(() => {
              const baseCard = ALL_CARDS[creature.cardId] as CreatureCard;
              if (!baseCard) return null;

              const powerMod = creature.power - (baseCard.power || 0);
              const healthMod = creature.health - (baseCard.health || 0);

              // Only show modifiers if they exist
              if (powerMod === 0 && healthMod === 0) return null;

              const statColor = (powerMod >= 0 && healthMod >= 0) ? 'success' : 'danger';
              const powerStr = powerMod >= 0 ? `+${powerMod}` : `${powerMod}`;
              const healthStr = healthMod >= 0 ? `+${healthMod}` : `${healthMod}`;

              return (
                <>
                  <IonBadge color={statColor}>
                    {powerStr}/{healthStr}
                  </IonBadge>
                </>
              );
            })()}
            <ShieldIndicator amount={getCreatureShield(gameState!, creature.instanceId)} compact />
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {creature.summoningSick && <IonBadge color="warning" style={{ fontSize: '0.7rem', padding: '2px 6px' }}>😴</IonBadge>}
            {creature.isTapped && <IonBadge color="medium">Tapped</IonBadge>}
            {isAttacking && <IonBadge color="danger"><IonIcon icon={flashOutline} /> Attacking</IonBadge>}
            {isTargeted && <IonBadge color="warning"><IonIcon icon={alertCircleOutline} /> Targeted</IonBadge>}
            {hasAdvantage && (
              <IonBadge color="success">
                <IonIcon icon={flash} /> Type Advantage!
              </IonBadge>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render mana source card
  const renderManaSource = (
    manaSource: BattlefieldManaSource,
    isPlayerManaSource: boolean = true,
    onClick?: () => void
  ) => {
    const card = ALL_CARDS[manaSource.cardId] as ManaSourceCard;
    if (!card) return null;

    return (
      <div
        key={manaSource.instanceId}
        className={`battlefield-mana-source ${manaSource.isTapped ? 'tapped' : ''}`}
        style={{ position: 'relative', display: 'inline-block' }}
      >
        <CardDisplay
          card={card}
          size="small"
          onClick={onClick}
          selected={false}
          disabled={false}
          showDetails={false}
        />
        <div style={{
          position: 'absolute',
          bottom: '4px',
          left: '4px',
          right: '4px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          {manaSource.isTapped && <IonBadge color="medium">Tapped</IonBadge>}
        </div>
      </div>
    );
  };

  // If no game started, show menu (skip in multiplayer mode)
  if (!gameState) {
    // In multiplayer mode, game state should always be provided
    if (isMultiplayer) {
      return <div>Loading game...</div>;
    }

    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => history.goBack()}>
                <IonIcon icon={arrowBack} />
              </IonButton>
            </IonButtons>
            <IonTitle>Crossroads: The Divergent Realms TCG</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="crossroads-setup-menu" style={{ padding: '16px' }}>
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Welcome to Crossroads TCG</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonText>
                  <p>Choose Your Path. Shape the Story.</p>
                  <p>A strategic trading card game where you command powerful Game Masters and battle using elemental forces.</p>
                </IonText>

                {/* Opponent Type Selection */}
                <IonItem>
                  <IonLabel>Opponent</IonLabel>
                  <IonSelect
                    value={opponentType}
                    onIonChange={(e) => setOpponentType(e.detail.value)}
                  >
                    <IonSelectOption value="human">Human</IonSelectOption>
                    <IonSelectOption value="ai">AI</IonSelectOption>
                  </IonSelect>
                </IonItem>

                {/* AI Difficulty Selection */}
                {opponentType === 'ai' && (
                  <IonItem>
                    <IonLabel>Difficulty</IonLabel>
                    <IonSelect
                      value={aiDifficulty}
                      onIonChange={(e) => setAIDifficulty(e.detail.value)}
                    >
                      <IonSelectOption value={AIDifficulty.EASY}>Easy</IonSelectOption>
                      <IonSelectOption value={AIDifficulty.MEDIUM}>Medium</IonSelectOption>
                      <IonSelectOption value={AIDifficulty.HARD}>Hard</IonSelectOption>
                    </IonSelect>
                  </IonItem>
                )}

                {/* Deck Selection */}
                <div style={{ marginTop: '16px' }}>
                  <h3>Decks</h3>

                  {/* Player 1 Deck */}
                  <div style={{ marginBottom: '12px' }}>
                    <IonItem>
                      <IonLabel>Player 1 Deck</IonLabel>
                    </IonItem>
                    {!customP1Deck ? (
                      <IonItem>
                        <IonLabel>Starter Deck</IonLabel>
                        <IonSelect
                          value={selectedP1StarterDeck}
                          onIonChange={(e) => setSelectedP1StarterDeck(e.detail.value)}
                        >
                          <IonSelectOption value="fire">Fire 🔥</IonSelectOption>
                          <IonSelectOption value="water">Water 💧</IonSelectOption>
                          <IonSelectOption value="earth">Earth 🌱</IonSelectOption>
                          <IonSelectOption value="wind">Wind 💨</IonSelectOption>
                          <IonSelectOption value="light">Light ☀️</IonSelectOption>
                          <IonSelectOption value="dark">Dark 🌑</IonSelectOption>
                          <IonSelectOption value="fae">Fae ✨</IonSelectOption>
                        </IonSelect>
                      </IonItem>
                    ) : (
                      <IonItem>
                        <IonLabel>
                          <h2>Custom Deck</h2>
                          <p>{customP1Deck.name}</p>
                        </IonLabel>
                      </IonItem>
                    )}
                    <IonItem lines="none">
                      <IonButton
                        slot="end"
                        size="small"
                        onClick={() => {
                          setDeckBuilderFor('p1');
                          setShowDeckBuilder(true);
                        }}
                      >
                        {customP1Deck ? 'Edit Custom' : 'Build Custom'}
                      </IonButton>
                      {customP1Deck && (
                        <IonButton
                          slot="end"
                          size="small"
                          fill="clear"
                          onClick={() => setCustomP1Deck(null)}
                        >
                          Use Starter
                        </IonButton>
                      )}
                    </IonItem>
                  </div>

                  {/* Player 2 Deck */}
                  <div style={{ marginBottom: '12px' }}>
                    <IonItem>
                      <IonLabel>Player 2 Deck</IonLabel>
                    </IonItem>
                    {!customP2Deck ? (
                      <IonItem>
                        <IonLabel>Starter Deck</IonLabel>
                        <IonSelect
                          value={selectedP2StarterDeck}
                          onIonChange={(e) => setSelectedP2StarterDeck(e.detail.value)}
                        >
                          <IonSelectOption value="fire">Fire 🔥</IonSelectOption>
                          <IonSelectOption value="water">Water 💧</IonSelectOption>
                          <IonSelectOption value="earth">Earth 🌱</IonSelectOption>
                          <IonSelectOption value="wind">Wind 💨</IonSelectOption>
                          <IonSelectOption value="light">Light ☀️</IonSelectOption>
                          <IonSelectOption value="dark">Dark 🌑</IonSelectOption>
                          <IonSelectOption value="fae">Fae ✨</IonSelectOption>
                        </IonSelect>
                      </IonItem>
                    ) : (
                      <IonItem>
                        <IonLabel>
                          <h2>Custom Deck</h2>
                          <p>{customP2Deck.name}</p>
                        </IonLabel>
                      </IonItem>
                    )}
                    <IonItem lines="none">
                      <IonButton
                        slot="end"
                        size="small"
                        onClick={() => {
                          setDeckBuilderFor('p2');
                          setShowDeckBuilder(true);
                        }}
                      >
                        {customP2Deck ? 'Edit Custom' : 'Build Custom'}
                      </IonButton>
                      {customP2Deck && (
                        <IonButton
                          slot="end"
                          size="small"
                          fill="clear"
                          onClick={() => setCustomP2Deck(null)}
                        >
                          Use Starter
                        </IonButton>
                      )}
                    </IonItem>
                  </div>

                  {/* Environment Deck Selection */}
                  <div style={{ marginBottom: '12px' }}>
                    <IonItem>
                      <IonLabel>Environment Deck</IonLabel>
                    </IonItem>
                    <IonItem>
                      <IonLabel>Theme</IonLabel>
                      <IonSelect
                        value={selectedEnvironmentDeck}
                        onIonChange={(e) => setSelectedEnvironmentDeck(e.detail.value)}
                      >
                        <IonSelectOption value="enchanted_forest">Enchanted Forest 🌲</IonSelectOption>
                        <IonSelectOption value="scorching_desert">Scorching Desert 🏜️</IonSelectOption>
                        <IonSelectOption value="frozen_tundra">Frozen Tundra ❄️</IonSelectOption>
                        <IonSelectOption value="stormy_skies">Stormy Skies ⛈️</IonSelectOption>
                        <IonSelectOption value="deep_ocean">Deep Ocean 🌊</IonSelectOption>
                        <IonSelectOption value="volcanic_wasteland">Volcanic Wasteland 🌋</IonSelectOption>
                        <IonSelectOption value="mystic_caverns">Mystic Caverns 🔮</IonSelectOption>
                        <IonSelectOption value="celestial_plains">Celestial Plains ✨</IonSelectOption>
                      </IonSelect>
                    </IonItem>
                  </div>
                </div>

                <IonButton expand="block" onClick={startGame} className="mt-4" style={{ marginTop: '16px' }}>
                  Start Game
                </IonButton>

                {/* Challenge Friend Button (only for Human opponent) */}
                {opponentType === 'human' && (
                  <IonButton
                    expand="block"
                    color="secondary"
                    onClick={() => setShowChallengeModal(true)}
                    style={{ marginTop: '8px' }}
                  >
                    <IonIcon icon={personOutline} slot="start" />
                    Challenge a Friend
                  </IonButton>
                )}
              </IonCardContent>
            </IonCard>
          </div>

          {/* Deck Builder - needed on setup screen */}
          <DeckBuilder
            isOpen={showDeckBuilder}
            onClose={() => setShowDeckBuilder(false)}
            onDeckComplete={(deck: DeckDefinition) => {
              if (deckBuilderFor === 'p1') {
                setCustomP1Deck(deck);
              } else {
                setCustomP2Deck(deck);
              }
              setShowDeckBuilder(false);
              showToast(`Deck "${deck.name}" created!`);
            }}
            initialDeck={deckBuilderFor === 'p1' ? customP1Deck || undefined : customP2Deck || undefined}
          />

          {/* Challenge Player Modal - needed on setup screen */}
          {showChallengeModal && (
            <ChallengePlayerModal
              isOpen={showChallengeModal}
              onDismiss={() => setShowChallengeModal(false)}
              gameType="Fire vs Water"
            />
          )}
        </IonContent>
      </IonPage>
    );
  }

  // Game content (will be wrapped differently based on mode)
  const gameContent = (
    <div className="crossroads-game" style={{ padding: '16px' }}>
        {/* Game Over */}
        {gameState.gameOver && (
          <IonCard className="game-over-card">
            <IonCardHeader>
              <IonCardTitle>Game Over!</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                {gameState.winner !== null
                  ? gameState.players[gameState.winner].name === 'You'
                    ? 'You win!'
                    : `${gameState.players[gameState.winner].name} wins!`
                  : 'Draw!'}
              </IonText>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <IonButton expand="block" onClick={() => setGameState(null)} fill="outline">
                  Back to Menu
                </IonButton>
                <IonButton expand="block" onClick={startGame}>
                  Play Again
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        )}

        {/* Game Status */}
        <div className="game-status">
          <IonCard>
            <IonCardContent>
              <div className="status-row">
                <div>
                  <IonText>
                    <strong>Round:</strong> {gameState.currentRound}
                  </IonText>
                </div>
                <div>
                  <IonBadge color={safeCurrentPlayer.id === 0 ? 'primary' : 'medium'}>
                    {gameState.currentPhase === 'environment'
                      ? 'Environment Phase'
                      : safeCurrentPlayer.name === 'You'
                        ? 'Your Turn'
                        : `${safeCurrentPlayer.name}'s Turn`}
                  </IonBadge>
                </div>
                <div>
                  <IonBadge color={(() => {
                    if (gameState.currentPhase === 'environment') {
                      return 'medium'; // Gray for environment
                    }
                    // Get current player's element type from commander card
                    const player = gameState.players[gameState.currentPlayerIndex];
                    const commanderCard = gameState.cardInstances[player.commander.cardId] as CommanderCard;
                    const elementType = commanderCard?.elementType || 'neutral';
                    const elementColorMap: Record<string, string> = {
                      fire: 'danger',
                      water: 'primary',
                      earth: 'success',
                      dark: 'dark',
                      light: 'warning',
                      fae: 'secondary',
                      wind: 'tertiary',
                      neutral: 'medium',
                    };
                    return elementColorMap[elementType] || 'warning';
                  })()}>
                    {gameState.currentPhase}
                  </IonBadge>
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        </div>

        {/* Chat Overlay - combines chat and event log */}
        <ChatOverlay
          events={gameState.eventLog || []}
          chatMessages={gameState.chatMessages || []}
          currentPlayerId={playerIndex}
          currentPlayerName={safePlayer.name}
          onSendMessage={handleSendMessage}
        />

        {/* Opponent Info */}
        <div className="opponent-area">
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{safeOpponent.name}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div
                  style={{
                    flexShrink: 0,
                    cursor: 'pointer',
                    border: selectedCombatAttacker && gameState.currentPhase === Phase.CONFLICT ? '2px solid #ff3b30' : 'none',
                    borderRadius: '8px',
                    padding: '2px'
                  }}
                  onClick={() => {
                    if (selectedCombatAttacker && gameState.currentPhase === Phase.CONFLICT) {
                      handleCombatTarget('player', String(safeOpponent.id));
                    } else {
                      // Allow viewing opponent's GM card
                      setCardDetailId(safeOpponent.commander.cardId);
                      setCardDetailModalOpen(true);
                    }
                  }}
                >
                  <CardDisplay card={safeOpponentCommanderCard} size="small" showDetails={false} lazy={false} />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="commander-info">
                    <IonIcon icon={heart} />
                    <IonText>{safeOpponent.commander.currentHealth} / {safeOpponent.commander.maxHealth}</IonText>
                    <ShieldIndicator amount={getPlayerShield(gameState, safeOpponent.id)} compact />
                  </div>
                  <div className="hand-count">
                    <IonBadge>Hand: {safeOpponent.hand.length}</IonBadge>
                    <IonBadge>Deck: {safeOpponent.deck.length}</IonBadge>
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Opponent Battlefield */}
          <div className="battlefield">
            <IonText>
              <h3>Opponent's Battlefield</h3>
            </IonText>

            {/* Mana Sources Zone */}
            {safeOpponent.battlefield.manaSources.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <IonText>
                  <strong>Mana Sources:</strong>
                </IonText>
                <div className="mana-source-zone" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {safeOpponent.battlefield.manaSources.map((manaSource: BattlefieldManaSource) =>
                    renderManaSource(
                      manaSource,
                      false,
                      () => {
                        // Allow viewing opponent's mana sources
                        setCardDetailId(manaSource.cardId);
                        setCardDetailModalOpen(true);
                      }
                    )
                  )}
                </div>
              </div>
            )}

            {/* Creatures Zone */}
            <div className="creature-zone">
              {/* Regular Creatures */}
              {safeOpponent.battlefield.creatures
                .filter((creature: BattlefieldCreature) => !(creature as any).isToken)
                .map((creature: BattlefieldCreature) =>
                  renderBattlefieldCreature(
                    creature,
                    false,
                    selectedCombatAttacker && gameState.currentPhase === Phase.CONFLICT
                      ? () => handleCombatTarget('creature', creature.instanceId)
                      : () => {
                          setCardDetailId(creature.cardId);
                          setCreatureInstanceId(creature.instanceId);
                          setCardDetailModalOpen(true);
                        }
                  )
                )}

              {/* Tokens (displayed below regular creatures) */}
              {safeOpponent.battlefield.creatures.filter((creature: BattlefieldCreature) => (creature as any).isToken).length > 0 && (
                <div style={{ width: '100%', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--ion-color-medium)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {safeOpponent.battlefield.creatures
                    .filter((creature: BattlefieldCreature) => (creature as any).isToken)
                    .map((creature: BattlefieldCreature) =>
                      renderBattlefieldCreature(
                        creature,
                        false,
                        selectedCombatAttacker && gameState.currentPhase === Phase.CONFLICT
                          ? () => handleCombatTarget('creature', creature.instanceId)
                          : () => {
                              setCardDetailId(creature.cardId);
                              setCreatureInstanceId(creature.instanceId);
                              setCardDetailModalOpen(true);
                            }
                      )
                    )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Environment Card */}
        {gameState.activeEnvironmentCard && (() => {
          const envCard = ALL_ENVIRONMENT_CARDS[gameState.activeEnvironmentCard];
          if (!envCard) return null;

          // Get severity color
          const getSeverityColor = (severity: string) => {
            switch (severity) {
              case 'mild': return 'success';
              case 'moderate': return 'warning';
              case 'chaos': return 'danger';
              case 'game_changing': return 'danger';
              default: return 'medium';
            }
          };

          return (
            <div className="environment-zone">
              <IonCard className="environment-card">
                <IonCardHeader>
                  <IonCardTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <span>🌍 {envCard.name}</span>
                    <IonBadge color={getSeverityColor(envCard.severity)} style={{ fontSize: '0.75rem' }}>
                      {envCard.severity.toUpperCase()}
                    </IonBadge>
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div style={{ marginBottom: '8px' }}>
                    <IonBadge color="medium" style={{ fontSize: '0.7rem' }}>
                      {envCard.deckTheme}
                    </IonBadge>
                    {envCard.targetType && envCard.targetType !== 'universal' && (
                      <IonBadge color="tertiary" style={{ marginLeft: '8px', fontSize: '0.7rem' }}>
                        {envCard.targetType === 'first_player' ? 'First Player' :
                         envCard.targetType === 'random' ? 'Random Target' :
                         String(envCard.targetType).toUpperCase()}
                      </IonBadge>
                    )}
                  </div>
                  {envCard.flavorText && (
                    <IonText className="flavor-text">
                      <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontStyle: 'italic', marginTop: '8px' }}>
                        "{envCard.flavorText}"
                      </p>
                    </IonText>
                  )}
                </IonCardContent>
              </IonCard>
            </div>
          );
        })()}

        {/* Player Battlefield */}
        <div className="player-area">
          <div className="battlefield">
            <IonText>
              <h3>Your Battlefield</h3>
            </IonText>

            {/* Mana Sources Zone */}
            {safePlayer.battlefield.manaSources.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <IonText>
                  <strong>Mana Sources:</strong>
                </IonText>
                <div className="mana-source-zone" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {safePlayer.battlefield.manaSources.map((manaSource: BattlefieldManaSource) =>
                    renderManaSource(
                      manaSource,
                      true,
                      () => {
                        // Open card detail modal instead of tapping directly
                        setCardDetailId(manaSource.cardId);
                        setManaSourceInstanceId(manaSource.instanceId);
                        setCardDetailModalOpen(true);
                      }
                    )
                  )}
                </div>
              </div>
            )}

            {/* Creatures Zone */}
            <div className="creature-zone">
              {/* Regular Creatures */}
              {safePlayer.battlefield.creatures
                .filter((creature: BattlefieldCreature) => !(creature as any).isToken)
                .map((creature: BattlefieldCreature) =>
                  renderBattlefieldCreature(
                    creature,
                    true,
                    gameState.currentPhase === Phase.CONFLICT
                      ? () => handleSelectCombatAttacker(creature.instanceId)
                      : () => {
                          setCardDetailId(creature.cardId);
                          setCreatureInstanceId(creature.instanceId);
                          setCardDetailModalOpen(true);
                        }
                  )
                )}

              {/* Tokens (displayed below regular creatures) */}
              {safePlayer.battlefield.creatures.filter((creature: BattlefieldCreature) => (creature as any).isToken).length > 0 && (
                <div style={{ width: '100%', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--ion-color-medium)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {safePlayer.battlefield.creatures
                    .filter((creature: BattlefieldCreature) => (creature as any).isToken)
                    .map((creature: BattlefieldCreature) =>
                      renderBattlefieldCreature(
                        creature,
                        true,
                        gameState.currentPhase === Phase.CONFLICT
                          ? () => handleSelectCombatAttacker(creature.instanceId)
                          : () => {
                              setCardDetailId(creature.cardId);
                              setCreatureInstanceId(creature.instanceId);
                              setCardDetailModalOpen(true);
                            }
                      )
                    )}
                </div>
              )}
            </div>

            {/* Mana Pool Display */}
            <ManaPoolDisplay
              manaPool={safePlayer.manaPool}
              manaSources={safePlayer.battlefield.manaSources}
              compact={false}
            />
          </div>

          {/* Commander */}
          <IonCard className="commander-card">
            <IonCardHeader>
              <IonCardTitle>{safeCommanderCard.name}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <div style={{ flexShrink: 0 }}>
                  <CardDisplay card={safeCommanderCard} size="medium" showDetails={false} lazy={false} />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="commander-info">
                    <IonIcon icon={heart} color="danger" />
                    <IonText>
                      <strong>{safePlayer.commander.currentHealth} / {safePlayer.commander.maxHealth}</strong>
                    </IonText>
                    <ShieldIndicator amount={getPlayerShield(gameState, safePlayer.id)} compact />
                  </div>
                  <IonText className="commander-subtitle">
                    <p>{safeCommanderCard.subtitle}</p>
                  </IonText>
                </div>
              </div>

              {/* Commander Abilities */}
              <div className="commander-abilities">
                <IonText>
                  <h4>Abilities:</h4>
                </IonText>
                {safeCommanderCard.abilities.map((ability: CommanderAbility, index: number) => {
                  const canAfford = GameLogic.canAffordCost(safePlayer.manaPool, ability.cost);
                  const usesThisTurn = safePlayer.commander.abilityUsesThisTurn[ability.id] || 0;
                  const canUse = !ability.usesPerTurn || usesThisTurn < ability.usesPerTurn;
                  const isTapped = safePlayer.commander.tapped;

                  // Phase restrictions - abilities can only be used in RISING_ACTION and RESOLUTION phases
                  const canUseInPhase =
                    gameState.currentPhase === Phase.RISING_ACTION ||
                    gameState.currentPhase === Phase.RESOLUTION;

                  // NEW RESTRICTIONS:
                  // 1. Commander must not be tapped to use ANY ability
                  const commanderTapped = isTapped;

                  // 2. Check if any tap ability has been used this turn
                  const tapAbilityUsedThisTurn = safeCommanderCard.abilities
                    .filter((a: CommanderAbility) => a.tapCost)
                    .some((a: CommanderAbility) => (safePlayer.commander.abilityUsesThisTurn[a.id] || 0) > 0);

                  // 3. If this is a spell-like ability (not tap), it can't be used after a tap ability
                  const spellAfterTap = !ability.tapCost && tapAbilityUsedThisTurn;

                  const isDisabled =
                    !canAfford ||
                    !canUse ||
                    commanderTapped ||
                    spellAfterTap ||
                    !canUseInPhase ||
                    safeCurrentPlayer.id !== 0;

                  return (
                    <IonButton
                      key={ability.id}
                      expand="block"
                      size="small"
                      onClick={() => handleActivateCommanderAbility(ability.id)}
                      disabled={isDisabled}
                      color={!isDisabled ? 'primary' : 'medium'}
                      className="ability-button"
                    >
                      <div className="ability-content">
                        <div className="ability-cost">
                          {Object.keys(ability.cost).length === 0 ? (
                            <IonBadge color="success">Free</IonBadge>
                          ) : (
                            Object.entries(ability.cost).map(([type, amount]: [string, number]) => (
                              <IonBadge key={type} color="medium">
                                <IonIcon icon={getElementIcon(type as ElementType)} /> {amount}
                              </IonBadge>
                            ))
                          )}
                        </div>
                        <IonText className="ability-description">{ability.description}</IonText>
                        {ability.usesPerTurn && (
                          <IonBadge color="warning">
                            {usesThisTurn}/{ability.usesPerTurn} used
                          </IonBadge>
                        )}
                        {ability.tapCost && (
                          <IonBadge color={isTapped ? 'danger' : 'secondary'}>
                            {isTapped ? '⟲ Tapped' : '↻ Tap'}
                          </IonBadge>
                        )}
                      </div>
                    </IonButton>
                  );
                })}
              </div>
            </IonCardContent>
          </IonCard>

          {/* Player Hand */}
          <div className="hand-zone">
            <IonText>
              <h3>Your Hand ({safePlayer.hand.length})</h3>
            </IonText>
            <div className="hand-cards">
              {safePlayer.hand.map((cardId: string, index: number) => {
                return renderCard(cardId, () => {
                  // Show card detail modal instead of immediately playing
                  setCardDetailId(cardId);
                  setCardDetailModalOpen(true);
                }, index);
              })}
            </div>

            {/* Old code removed - now using card detail modal */}
          </div>

          {/* Controls */}
          <div className="game-controls">
            <IonButton
              expand="block"
              onClick={handleNextPhase}
              disabled={safeCurrentPlayer.id !== 0 || gameState.gameOver}
            >
              {gameState.currentPhase === Phase.RESOLUTION ? 'End Turn' : 'Next Phase'}
            </IonButton>

            {/* Discard & Redraw button - only show during OPENING phase before actions */}
            {safeCurrentPlayer.id === 0 &&
              !gameState.gameOver &&
              gameState.currentPhase === Phase.OPENING &&
              safePlayer.hand.length > 0 &&
              !safePlayer.hasPlayedManaThisTurn && (
                <IonButton
                  expand="block"
                  color="warning"
                  fill="outline"
                  onClick={handleDiscardAndRedraw}
                  style={{ marginTop: '8px' }}
                >
                  Discard Hand & Redraw (Skip Turn)
                </IonButton>
              )}
          </div>
        </div>

        {/* Combat Help Alert */}
        <IonAlert
          isOpen={showCombatHelp}
          onDidDismiss={() => setShowCombatHelp(false)}
          header="Combat Rules"
          message={`COMBAT FLOW:
1. Declare attackers (tap creatures to attack)
2. Assign targets (commander or creatures)
3. Opponent declares blockers (automated)
4. Combat resolves simultaneously

TYPE ADVANTAGE:
Attacking with type advantage gives +1 power!

Fire ⚡ Wind, Earth
Water ⚡ Fire, Dark

DAMAGE:
• Unblocked attackers deal damage to their target
• Blocked combat is resolved between creatures
• Creatures with 0 or less health are defeated`}
          buttons={['Got it!']}
        />

        {/* Mulligan Prompt */}
        <IonAlert
          isOpen={showMulliganPrompt}
          onDidDismiss={() => setShowMulliganPrompt(false)}
          header="Mulligan?"
          message="Would you like to discard your hand and draw 7 new cards? (You will skip your first turn)"
          buttons={[
            {
              text: 'Keep Hand',
              role: 'cancel',
              handler: () => {
                setShowMulliganPrompt(false);
              }
            },
            {
              text: 'Mulligan',
              handler: () => {
                handleDiscardAndRedraw();
                setShowMulliganPrompt(false);
              }
            }
          ]}
        />

        {/* Challenge Player Modal - only in single player mode */}
        {!isMultiplayer && (
          <ChallengePlayerModal
            isOpen={showChallengeModal}
            onDismiss={() => setShowChallengeModal(false)}
            gameType="Fire vs Water"
          />
        )}

        {/* Dice Roll Indicators */}
        {activeDiceRolls.map((roll) => (
          <DiceRollIndicator
            key={roll.id}
            value={roll.value}
            isReroll={roll.isReroll}
            onComplete={() => handleDiceRollComplete(roll.id)}
          />
        ))}

        {/* Tutorial Overlay */}
        {showTutorial && (
          <TutorialOverlay
            steps={tutorialSteps}
            onComplete={handleTutorialComplete}
            onSkip={handleTutorialSkip}
          />
        )}

        {/* Modal Choice Dialog - Only show if it's this player's choice */}
        {(() => {
          if (gameState?.pendingModalChoice) {
            logger.log('📋 Modal Choice Detected:', {
              playerId: gameState.pendingModalChoice.playerId,
              playerIndex,
              isMultiplayer,
              title: gameState.pendingModalChoice.title,
              type: gameState.pendingModalChoice.type,
              optionsCount: gameState.pendingModalChoice.options?.length || 0,
              hasOnComplete: !!gameState.pendingModalChoice.onComplete,
              description: gameState.pendingModalChoice.description,
              shouldShowModal: gameState.pendingModalChoice.playerId === playerIndex || !isMultiplayer
            });
          }
          const shouldShowModal = gameState?.pendingModalChoice &&
            gameState.pendingModalChoice.playerId === playerIndex;
          return (
            <ModalChoiceDialog
              modalChoice={gameState?.pendingModalChoice || null}
              isOpen={!!shouldShowModal}
              onChoiceComplete={() => {
                // Trigger re-render after modal choice is completed
                if (gameState) {
                  setGameState({ ...gameState });
                }
              }}
            />
          );
        })()}

        {/* Target Selector - Only show if it's this player's turn to target */}
        {(() => {
          if (gameState?.pendingTargetSelection) {
            logger.log('🎯 Target Selection Detected:', {
              playerId: gameState.pendingTargetSelection.playerId,
              playerIndex,
              isMultiplayer,
              title: gameState.pendingTargetSelection.title,
              targetType: gameState.pendingTargetSelection.targetType
            });
          }
          return gameState?.pendingTargetSelection && (!isMultiplayer || gameState.pendingTargetSelection.playerId === playerIndex) ? (
            <TargetSelector
              gameState={gameState}
              targetRequest={gameState.pendingTargetSelection}
            onComplete={(selection: any) => {
              if (gameState.pendingTargetSelection) {
                gameState.pendingTargetSelection.onComplete(selection);
                gameState.pendingTargetSelection = undefined;
                setGameState({ ...gameState });
              }
            }}
            onCancel={() => {
              if (gameState.pendingTargetSelection?.onCancel) {
                gameState.pendingTargetSelection.onCancel();
              }
              if (gameState) {
                gameState.pendingTargetSelection = undefined;
                setGameState({ ...gameState });
              }
            }}
          />
          ) : null;
        })()}

        {/* Deck Builder */}
        <DeckBuilder
          isOpen={showDeckBuilder}
          onClose={() => setShowDeckBuilder(false)}
          onDeckComplete={(deck: DeckDefinition) => {
            if (deckBuilderFor === 'p1') {
              setCustomP1Deck(deck);
            } else {
              setCustomP2Deck(deck);
            }
            setShowDeckBuilder(false);
            showToast(`Deck "${deck.name}" created!`);
          }}
          initialDeck={deckBuilderFor === 'p1' ? customP1Deck || undefined : customP2Deck || undefined}
        />

        {/* Card Detail Modal */}
        <CardDetailModal
          card={cardDetailId ? ALL_CARDS[cardDetailId] : null}
          isOpen={cardDetailModalOpen}
          copiedAbilities={
            creatureInstanceId && gameState
              ? [...gameState.players[0].battlefield.creatures, ...gameState.players[1].battlefield.creatures]
                  .find((c) => c.instanceId === creatureInstanceId)?.copiedAbilities
              : undefined
          }
          onDismiss={() => {
            setCardDetailModalOpen(false);
            setManaSourceInstanceId(null);
            setCreatureInstanceId(null);
          }}
          onPlay={handlePlayCardFromModal}
          canPlay={gameState !== null && canTakeAction() && cardDetailId !== null && canPlayCard(cardDetailId)}
          showPlayButton={
            manaSourceInstanceId === null &&
            creatureInstanceId === null &&
            gameState !== null &&
            cardDetailId !== null &&
            gameState.players[0].hand.includes(cardDetailId)
          }
          onTap={() => {
            if (gameState && manaSourceInstanceId) {
              // Tap mana source
              const newGame = deepClone(gameState);
              if (GameLogic.tapManaSource(newGame, 0, manaSourceInstanceId)) {
                setGameState(newGame);
              }
            } else if (gameState && creatureInstanceId) {
              // Tap creature to activate ability
              const newGame = deepClone(gameState);
              const creature = [...newGame.players[0].battlefield.creatures, ...newGame.players[1].battlefield.creatures].find(
                (c) => c.instanceId === creatureInstanceId
              );

              if (creature && creature.copiedAbilities) {
                const tapAbility = creature.copiedAbilities.find(
                  (ability) => ability.trigger === 'activated' && ability.tapCost
                );

                if (tapAbility && !creature.isTapped) {
                  // Tap the creature
                  creature.isTapped = true;

                  // Execute the ability
                  tapAbility.effect(newGame, creatureInstanceId);

                  setGameState(newGame);
                  setCardDetailModalOpen(false);
                  setCreatureInstanceId(null);
                }
              }
            }
          }}
          canTap={
            gameState !== null &&
            gameState.currentPlayerIndex === 0 &&
            gameState.currentPhase === Phase.RISING_ACTION &&
            (() => {
              // Check mana source
              if (manaSourceInstanceId) {
                const manaSource = gameState.players[0].battlefield.manaSources.find(
                  (m) => m.instanceId === manaSourceInstanceId
                );
                return manaSource ? !manaSource.isTapped : false;
              }

              // Check creature tap ability
              if (creatureInstanceId) {
                const creature = [...gameState.players[0].battlefield.creatures, ...gameState.players[1].battlefield.creatures].find(
                  (c) => c.instanceId === creatureInstanceId
                );

                if (creature && creature.copiedAbilities) {
                  const tapAbility = creature.copiedAbilities.find(
                    (ability) => ability.trigger === 'activated' && ability.tapCost
                  );
                  return tapAbility ? !creature.isTapped : false;
                }
              }

              return false;
            })()
          }
          showTapButton={manaSourceInstanceId !== null || creatureInstanceId !== null}
        />

        {/* Combat Preview */}
        {showCombatPreview && gameState && (
          <CombatPreview
            attackers={combatAttackers}
            blockers={combatBlockers}
            playerCreatures={gameState.players[playerIndex].battlefield.creatures}
            opponentCreatures={gameState.players[1 - playerIndex].battlefield.creatures}
            playerCommanderHealth={gameState.players[playerIndex].commanderHealth ?? gameState.players[playerIndex].commander.currentHealth}
            opponentCommanderHealth={gameState.players[1 - playerIndex].commanderHealth ?? gameState.players[1 - playerIndex].commander.currentHealth}
            onConfirm={handleResolveCombat}
            onCancel={() => setShowCombatPreview(false)}
            isPlayerAttacking={true}
            gameState={gameState}
          />
        )}
        </div>
  );

  // Wrap with IonPage only in single-player mode
  if (isMultiplayer) {
    return gameContent;
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => setGameState(null)}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Crossroads: The Divergent Realms TCG</IonTitle>
          <IonButtons slot="end">
            <IonButton id="game-menu-trigger" title="Game Menu">
              <IonIcon icon={ellipsisVertical} />
            </IonButton>
            <IonButton onClick={() => setShowTutorial(true)} title="Show Tutorial">
              <IonIcon icon={helpCircleOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {gameContent}
      </IonContent>

      {/* Game Menu Popover */}
      <IonPopover
        trigger="game-menu-trigger"
        dismissOnSelect={true}
      >
        <IonList>
          <IonItem
            button
            onClick={() => {
              if (opponentType === 'ai') {
                // For AI games, just end the game
                setGameState(null);
                showToast('Game ended', 'info');
              } else {
                // For human games, show forfeit confirmation
                setShowForfeitAlert(true);
              }
            }}
          >
            <IonIcon icon={opponentType === 'ai' ? exitOutline : flagOutline} slot="start" />
            <IonLabel>{opponentType === 'ai' ? 'End Game' : 'Forfeit'}</IonLabel>
          </IonItem>
        </IonList>
      </IonPopover>

      {/* Forfeit Confirmation Alert */}
      <IonAlert
        isOpen={showForfeitAlert}
        onDidDismiss={() => setShowForfeitAlert(false)}
        header="Forfeit Game"
        message="Are you sure you want to forfeit this game? Your opponent will win."
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Forfeit',
            role: 'destructive',
            handler: () => {
              setGameState(null);
              showToast('You forfeited the game', 'warning');
            },
          },
        ]}
      />
    </IonPage>
  );
};

export default Crossroads;
